import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Keyboard, View } from 'react-native';

import { createMqStyles, tokens } from '@fhs/ui';
import { IRestaurantNode } from '@fhs-legacy/frontend/src/generated/rbi-graphql';
import useMap from '@fhs-legacy/frontend/src/hooks/use-map';
import { IUseMapArgs, MarkerTypes } from '@fhs-legacy/frontend/src/hooks/use-map/types';
import useMarkers from '@fhs-legacy/frontend/src/pages/store-locator/hooks/use-markers';
import { useGeolocation } from '@fhs-legacy/frontend/src/state/geolocation';
import { ILocation } from '@rbi-ctg/frontend';

import { MapSearchOverlay } from '../search-field';

import { DELIVERY_ADDRESS_ZOOM, SELECTED_STORE_ZOOM } from './constants';
import { MapControls } from './map-controls';
import { SearchThisAreaButton } from './search-this-area-button';
import { MapFocus, useMapFocus } from './use-map-focus';
import { useSearchThisArea } from './use-search-this-area';

interface MapPaneProps {
  /** Every known restaurant, so pins stay put while the guest switches tabs. */
  restaurants: IRestaurantNode[];
  favoriteRestaurants: IRestaurantNode[];
  selectedRestaurantId: string | null;
  /** Incremented on every store select so a repeat tap still recenters the map. */
  selectionFocusVersion: number;
  /** True while the selected store is still loading, so the map holds its first frame. */
  isSelectionLoading?: boolean;
  onSelectRestaurant: (restaurantId: string) => void;
  onClearSelection: () => void;
  /** Small screens overlay the collapsible search on the map; desktop keeps it in the panel. */
  showSearchOverlay: boolean;
  /** Desktop keeps the search in the panel, so locate-me has to clear that field from here. */
  onSearchReset?: () => void;
  /** Called when the guest picks an address in the overlay search. */
  onSelectLocation: () => void;
  /** Delivery has no nearby stores to re-search, so it hides the area search. */
  showSearchThisArea?: boolean;
  /** An area search refetches the nearby stores, which only one tab shows. */
  onSearchThisArea?: () => void;
  /**
   * Shows the map as a picture of where the store is rather than something to explore: no
   * gestures, and none of the controls that would move it or search somewhere else. Store
   * details asks for this, since the map behind it is only there to place the one store.
   */
  isStatic?: boolean;
  /** Delivery pins the guest's chosen address instead of the stores around them. */
  deliveryAddressCoordinates?: ILocation | null;
  /** Delivery is fulfilled by a restaurant the guest doesn't pick, so its pins are hidden. */
  hideStoreMarkers?: boolean;
}

const NO_RESTAURANTS: IRestaurantNode[] = [];

/**
 * Web still runs on the legacy Google Maps JS hook. The replacement in `@fhs/maps` is native
 * only for now, so this file stays as the web half of the platform split and should be
 * retired once `@fhs/maps` grows a web implementation.
 */
export const MapPane: React.VFC<MapPaneProps> = ({
  restaurants,
  favoriteRestaurants,
  selectedRestaurantId,
  selectionFocusVersion,
  isSelectionLoading = false,
  onSelectRestaurant,
  onClearSelection,
  showSearchOverlay,
  onSearchReset,
  onSelectLocation,
  showSearchThisArea = false,
  onSearchThisArea,
  isStatic = false,
  deliveryAddressCoordinates = null,
  hideStoreMarkers = false,
}) => {
  const styles = useMapPaneMqStyles();
  const { activeCoordinates, resetCoordsAndLoadCurrentPosition } = useGeolocation();
  const [searchDismissSignal, setSearchDismissSignal] = useState(0);
  const [searchClearSignal, setSearchClearSignal] = useState(0);

  // Nothing blurs a React Native TextInput on its own, so every control that counts as
  // "outside" the search has to put the keyboard away itself. The signal covers web, where
  // there is no keyboard to dismiss but the field still holds focus.
  const dismissSearch = useCallback(() => {
    setSearchDismissSignal(signal => signal + 1);
    Keyboard.dismiss();
  }, []);

  // The map binds its listeners once, when it is created, so anything they need to know has
  // to be read at the time of the event rather than closed over from the render that bound it.
  const isStaticRef = useRef(isStatic);
  isStaticRef.current = isStatic;
  const hideStoreMarkersRef = useRef(hideStoreMarkers);
  hideStoreMarkersRef.current = hideStoreMarkers;

  const onMapPress = useCallback(() => {
    // A static map has nothing to deselect: details is showing the one store its pin marks.
    // Nor does one without store pins, where the selection is Pickup's and has to still be
    // there when the guest goes back to it.
    if (!isStaticRef.current && !hideStoreMarkersRef.current) {
      onClearSelection();
    }

    dismissSearch();
  }, [dismissSearch, onClearSelection]);

  const onAreaSearched = useCallback(() => {
    // The selection belongs to the area the guest has left, and leaving it selected keeps its
    // large pin active in the map SDK — the same reason locate-me clears it.
    onClearSelection();
    dismissSearch();
    setSearchClearSignal(signal => signal + 1);
    onSearchReset?.();
    onSearchThisArea?.();
  }, [dismissSearch, onClearSelection, onSearchReset, onSearchThisArea]);

  const { canSearchThisArea, onMapMovedByUser, searchArea } = useSearchThisArea({
    onSearch: onAreaSearched,
  });

  const eventListeners: IUseMapArgs['eventListeners'] = useMemo(
    () => ({
      // Native MapView uses onPress; the Google Maps JS API uses click.
      onPress: onMapPress,
      click: onMapPress,
      // Wheel and pinch zooming keep the centre where it is, so a drag is the only gesture
      // that leaves the map showing somewhere other than what was searched.
      dragend: onMapMovedByUser,
    }),
    [onMapPress, onMapMovedByUser]
  );

  // Selecting a card must move the map, so follow the shared selection wherever it came from.
  const selectedRestaurant = restaurants.find(
    restaurant => restaurant._id === selectedRestaurantId
  );
  const selectedLat = selectedRestaurant?.latitude;
  const selectedLng = selectedRestaurant?.longitude;
  const selectedCoordinates = useMemo(
    () =>
      selectedLat != null && selectedLng != null ? { lat: selectedLat, lng: selectedLng } : null,
    [selectedLat, selectedLng]
  );

  const { focus, canOpenMap } = useMapFocus({
    guestCoordinates: activeCoordinates,
    selectedCoordinates,
    isSelectionLoading,
  });

  // The map keeps whatever frame it is created with, so it is created on the focus rather
  // than moved onto it afterwards — which is the difference between opening on a store and
  // opening on the guest and sliding away from them.
  const { center, createMarker, removeMarkers, map, zoomBy, panTo } = useMap(
    focus
      ? {
          eventListeners,
          position: focus.coordinates,
          initialZoom: focus.isSelectedStore ? SELECTED_STORE_ZOOM : undefined,
          disableControls: isStatic,
        }
      : { eventListeners, disableControls: isStatic }
  );

  const searchThisArea = useCallback(() => searchArea(center), [center, searchArea]);

  const onMarkerPress = useCallback(
    (store: IRestaurantNode) => {
      dismissSearch();
      if (store._id) {
        onSelectRestaurant(store._id);
      }
    },
    [dismissSearch, onSelectRestaurant]
  );

  // Hidden pins leave the same way as stores that drop out of the list: `useMarkers` is handed
  // none and the effect below takes the rest off. Handing the list back has it create every
  // pin afresh, so they return exactly as the guest left them.
  const markerRestaurants = hideStoreMarkers ? NO_RESTAURANTS : restaurants;

  useMarkers({
    createMarker,
    panTo,
    onPress: onMarkerPress,
    storesNearby: markerRestaurants,
    storesFavs: favoriteRestaurants,
    activeStoreId: selectedRestaurantId,
    useSelectedStorePin: true,
    useServiceModeOpenState: true,
  });

  const storeIds = useMemo(
    () => markerRestaurants.map(({ _id }) => _id).filter((id): id is string => Boolean(id)),
    [markerRestaurants]
  );

  // `useMarkers` only ever adds pins, so the stores that have left the list are still on the
  // map until they are taken off — which is what store details narrowing to one store needs.
  // Declared after it so the pins for the stores that stayed are already in place.
  const pinnedStoreIds = useRef<string[]>([]);
  useEffect(() => {
    const dropped = pinnedStoreIds.current.filter(id => !storeIds.includes(id));
    pinnedStoreIds.current = storeIds;

    if (dropped.length) {
      removeMarkers?.(dropped);
    }
  }, [removeMarkers, storeIds]);

  // Moves the map onto the focus, framing a store closer than a plain recenter.
  // `selectionFocusVersion` brings the guest back to a store they click again after having
  // dragged away from it.
  useEffect(() => {
    if (!focus) {
      return;
    }

    panTo(focus.coordinates, focus.isSelectedStore ? { zoom: SELECTED_STORE_ZOOM } : undefined);
  }, [focus, panTo, selectionFocusVersion]);

  // The house pin stands in for the store pins rather than joining them, so an address the
  // guest picked stays off the map while Pickup is showing.
  const deliveryLat = hideStoreMarkers ? deliveryAddressCoordinates?.lat : undefined;
  const deliveryLng = hideStoreMarkers ? deliveryAddressCoordinates?.lng : undefined;
  const deliveryCoordinates = useMemo(
    () =>
      deliveryLat != null && deliveryLng != null ? { lat: deliveryLat, lng: deliveryLng } : null,
    [deliveryLat, deliveryLng]
  );

  useEffect(() => {
    if (deliveryCoordinates) {
      createMarker({ type: MarkerTypes.DeliveryAddress, location: deliveryCoordinates });
      return;
    }

    // The map keeps a single-use marker under its type, so that is the id it comes off by.
    removeMarkers?.([MarkerTypes.DeliveryAddress]);
  }, [createMarker, deliveryCoordinates, removeMarkers]);

  // Where the map goes back to once a delivery address lets go of it. Back on Pickup that is
  // the focus. On Delivery it is the guest: a store picked on Pickup has no pin here, so
  // returning to it would only show an empty street. Read at the moment the address goes
  // rather than followed, since moving with the focus after that is the focus effect's job.
  const guestFocus: MapFocus | null = activeCoordinates
    ? { coordinates: activeCoordinates, isSelectedStore: false }
    : null;
  const fallbackFocusRef = useRef(focus);
  fallbackFocusRef.current = hideStoreMarkers ? guestFocus : focus;

  // A delivery address moves the map the way a picked store does, and lets go of it when the
  // guest clears it or goes back to Pickup. Only an address that was there can let go, so a
  // map that never had one is left to the focus alone.
  const hadDeliveryAddress = useRef(false);
  useEffect(() => {
    if (deliveryCoordinates) {
      hadDeliveryAddress.current = true;
      panTo(deliveryCoordinates, { zoom: DELIVERY_ADDRESS_ZOOM });
      return;
    }

    if (!hadDeliveryAddress.current) {
      return;
    }

    hadDeliveryAddress.current = false;
    const fallbackFocus = fallbackFocusRef.current;
    if (fallbackFocus) {
      panTo(
        fallbackFocus.coordinates,
        fallbackFocus.isSelectedStore ? { zoom: SELECTED_STORE_ZOOM } : undefined
      );
    }
  }, [deliveryCoordinates, panTo]);

  // Recenter must drop the selected store. Leaving it selected keeps the large pin
  // "active" in the map SDK, so a second tap on that restaurant is ignored.
  const onRecenter = useCallback(() => {
    onClearSelection();
    dismissSearch();
    setSearchClearSignal(signal => signal + 1);
    onSearchReset?.();
    resetCoordsAndLoadCurrentPosition();
  }, [dismissSearch, onClearSelection, onSearchReset, resetCoordsAndLoadCurrentPosition]);

  const onZoomIn = useCallback(() => {
    dismissSearch();
    zoomBy?.(1);
  }, [dismissSearch, zoomBy]);
  const onZoomOut = useCallback(() => {
    dismissSearch();
    zoomBy?.(-1);
  }, [dismissSearch, zoomBy]);

  return (
    <View style={styles.container} testID="store-locator-map">
      {canOpenMap && map}

      {/* An area search looks for stores, so it goes wherever their pins go. */}
      {!isStatic && showSearchThisArea && !hideStoreMarkers && canSearchThisArea && (
        <SearchThisAreaButton onPress={searchThisArea} />
      )}

      {!isStatic && showSearchOverlay && (
        // The band spans the map so the expanded field can, but only its controls take touches.
        <View style={styles.searchOverlay} pointerEvents="box-none">
          <MapSearchOverlay
            dismissSignal={searchDismissSignal}
            clearSignal={searchClearSignal}
            onSelectLocation={onSelectLocation}
          />
        </View>
      )}

      {!isStatic && (
        <MapControls onZoomIn={onZoomIn} onZoomOut={onZoomOut} onRecenter={onRecenter} />
      )}
    </View>
  );
};

const useMapPaneMqStyles = createMqStyles({
  container: {
    $base: {
      backgroundColor: tokens.colors.$houseLight,
      flex: 1,
      overflow: 'hidden',
      position: 'relative',
    },
  },
  searchOverlay: {
    $base: {
      elevation: 6,
      left: 16,
      overflow: 'visible',
      position: 'absolute',
      right: 16,
      top: 16,
      zIndex: 1,
    },
  },
});
